import type { GraphQLClient } from 'graphql-request'

// Pulse notes — the room's knowledge shelf. Notes have NO server broadcast
// (NoteObserver only feeds the search index), so freshness comes from the
// GameView poller + refetch-on-open, not Pusher.

export interface NoteLite {
  id: string
  title: string
  content: string | null
  pinned: boolean
  createdAt: string
  updatedAt: string
}

const NOTES_QUERY = /* GraphQL */ `
  query GlassOfficeNotes($input: PaginatedNotesInput!) {
    paginatedNotes(input: $input) {
      data {
        id
        title
        content
        pinned
        createdAt
        updatedAt
      }
      paginatorInfo {
        total
      }
    }
  }
`

export async function fetchNotes(
  client: GraphQLClient,
  params: { organizationId: string; pulseId: string; perPage?: number },
): Promise<{ notes: NoteLite[]; total: number }> {
  const data = await client.request<{
    paginatedNotes?: {
      data?: Array<{
        id: string
        title?: string | null
        content?: string | null
        pinned?: boolean | null
        createdAt?: string | null
        updatedAt?: string | null
      }> | null
      paginatorInfo?: { total?: number | null } | null
    } | null
  }>(NOTES_QUERY, {
    input: {
      organizationId: params.organizationId,
      pulseId: params.pulseId,
      sortBy: 'UPDATED_AT',
      sortOrder: 'DESC',
      page: 1,
      perPage: params.perPage ?? 30,
    },
  })
  return {
    notes: (data.paginatedNotes?.data ?? []).map((n) => ({
      id: n.id,
      title: n.title ?? 'Untitled',
      content: n.content ?? null,
      pinned: Boolean(n.pinned),
      createdAt: n.createdAt ?? '',
      updatedAt: n.updatedAt ?? '',
    })),
    total: data.paginatedNotes?.paginatorInfo?.total ?? 0,
  }
}

const CREATE_NOTE = /* GraphQL */ `
  mutation GlassOfficeCreateNote($input: CreateNoteInput!) {
    createNote(input: $input) {
      id
    }
  }
`

export async function createNote(
  client: GraphQLClient,
  params: {
    organizationId: string
    pulseId: string
    userId: string
    title: string
    content?: string
  },
): Promise<{ id: string }> {
  const data = await client.request<{ createNote?: { id: string } }>(CREATE_NOTE, {
    input: {
      organizationId: params.organizationId,
      pulseId: params.pulseId,
      userId: params.userId,
      title: params.title,
      ...(params.content ? { content: params.content } : {}),
    },
  })
  if (!data.createNote?.id) throw new Error('Note was not created')
  return { id: data.createNote.id }
}

const UPDATE_NOTE = /* GraphQL */ `
  mutation GlassOfficeUpdateNote($input: UpdateNoteInput!) {
    updateNote(input: $input) {
      id
      title
      updatedAt
    }
  }
`

export async function updateNote(
  client: GraphQLClient,
  params: {
    organizationId: string
    pulseId: string
    noteId: string
    title: string
    content: string | null
  },
): Promise<void> {
  await client.request(UPDATE_NOTE, {
    input: {
      id: params.noteId,
      title: params.title,
      content: params.content,
      organizationId: params.organizationId,
      pulseId: params.pulseId,
    },
  })
}

const DELETE_NOTE = /* GraphQL */ `
  mutation GlassOfficeDeleteNote($noteId: ID!) {
    deleteNote(noteId: $noteId) {
      id
    }
  }
`

export async function deleteNote(
  client: GraphQLClient,
  noteId: string,
): Promise<void> {
  await client.request(DELETE_NOTE, { noteId })
}
